import React, { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
  adminCakeOptionProductOverrides,
  ProductOverrideInput,
  ProductOverridesState,
} from '../../api/adminCakeOptions';
import { formatCurrency } from '../../utils/currency';
import { getErrorDetail, resolveErrorBanner, ErrorBannerState } from '../../utils/errorDetail';

// Per-product view of the global Cake Options (task-191): every group linked to
// the product's category (or an ancestor) is listed; the admin can switch an
// item off for this product or give it a price override. An empty override
// input means NULL = use the item's default price. Hidden when no group applies.

const PanelLabel = styled.div`
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 8px;
`;
const Group = styled.div`
  padding: 12px;
  background: #FAF8F3;
  border: 1px solid #E8DDD0;
  border-radius: 6px;
  margin-bottom: 12px;
`;
const GroupTitle = styled.div`
  font-weight: 600;
  margin-bottom: 8px;
`;
const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 4px 0;
`;
const ItemLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
`;
const PriceInput = styled.input`
  width: 110px;
  padding: 6px 8px;
  border: 1px solid #E8DDD0;
  border-radius: 4px;
`;
const Hint = styled.span`
  font-size: 12px;
  color: #6B5E4E;
  white-space: nowrap;
`;
const SaveButton = styled.button`
  background: #10b981;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 8px 16px;
  cursor: pointer;
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;
const ErrorText = styled.div`
  color: #dc2626;
  font-size: 13px;
  margin-top: 8px;
`;
const SavedText = styled.div`
  color: #047857;
  font-size: 13px;
  margin-top: 8px;
`;

interface RowDraft {
  disabled: boolean;
  price: string; // '' = no override (NULL)
}

type Drafts = Record<number, RowDraft>;

const toDrafts = (state: ProductOverridesState): Drafts => {
  const drafts: Drafts = {};
  state.groups.forEach(g =>
    g.items.forEach(i => {
      drafts[i.id] = {
        disabled: i.disabled,
        price: i.price_override === null || i.price_override === undefined ? '' : String(i.price_override),
      };
    })
  );
  return drafts;
};

interface Props {
  productId: string;
}

const ProductCakeOptionsPanel: React.FC<Props> = ({ productId }) => {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const zh = (i18n.language || '').startsWith('zh');
  const queryKey = ['adminProductCakeOptions', productId];

  const { data, isLoading, isError } = useQuery({
    queryKey,
    queryFn: () => adminCakeOptionProductOverrides.get(productId),
  });

  const [drafts, setDrafts] = useState<Drafts>({});
  const [error, setError] = useState<ErrorBannerState | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (data) setDrafts(toDrafts(data));
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: (overrides: ProductOverrideInput[]) =>
      adminCakeOptionProductOverrides.update(productId, overrides),
    onSuccess: (fresh) => {
      queryClient.setQueryData(queryKey, fresh);
      setDrafts(toDrafts(fresh));
      setError(null);
      setSaved(true);
    },
    onError: (err: any) => {
      setSaved(false);
      setError({ raw: getErrorDetail(err), fallbackKey: 'admin.products.cakeOptions.saveFailed' });
    },
  });

  if (isLoading) return <div>{t('admin.products.cakeOptions.loading')}</div>;
  if (isError) return <ErrorText>{t('admin.products.cakeOptions.loadFailed')}</ErrorText>;
  if (!data || data.groups.length === 0) return null;

  const update = (id: number, patch: Partial<RowDraft>) => {
    setSaved(false);
    setDrafts(prev => ({ ...prev, [id]: { ...prev[id], ...patch } }));
  };

  const priceInvalid = (id: number) => {
    const p = drafts[id]?.price ?? '';
    if (p.trim() === '') return false;
    const n = Number(p);
    return !Number.isFinite(n);
  };
  const anyInvalid = data.groups.some(g => g.items.some(i => priceInvalid(i.id)));

  const handleSave = () => {
    const overrides: ProductOverrideInput[] = [];
    data.groups.forEach(g =>
      g.items.forEach(i => {
        const d = drafts[i.id];
        if (!d) return;
        overrides.push({
          option_item_id: i.id,
          disabled: d.disabled,
          price_override: d.price.trim() === '' ? null : Number(d.price),
        });
      })
    );
    saveMutation.mutate(overrides);
  };

  return (
    <div data-testid="product-cake-options-panel">
      <PanelLabel>{t('admin.products.cakeOptions.label')}</PanelLabel>
      {data.groups.map(g => (
        <Group key={g.id}>
          <GroupTitle>
            {zh && g.name_zh ? g.name_zh : g.name}
            {' '}
            <Hint>
              ({g.selection_mode === 'single' ? t('admin.products.cakeOptions.modeSingle') : t('admin.products.cakeOptions.modeMultiple')}
              {g.required ? `, ${t('admin.products.cakeOptions.required')}` : ''})
            </Hint>
          </GroupTitle>
          {g.items.map(i => {
            const label = zh && i.name_zh ? i.name_zh : i.name;
            const d = drafts[i.id] || { disabled: i.disabled, price: '' };
            return (
              <Row key={i.id}>
                <ItemLabel>
                  <input
                    type="checkbox"
                    checked={!d.disabled}
                    onChange={(e) => update(i.id, { disabled: !e.target.checked })}
                    aria-label={t('admin.products.cakeOptions.enabledAria', { name: label })}
                  />
                  {label}
                </ItemLabel>
                <Hint>{t('admin.products.cakeOptions.defaultPrice', { price: formatCurrency(i.default_price) })}</Hint>
                <PriceInput
                  type="number"
                  step="0.01"
                  placeholder={t('admin.products.cakeOptions.pricePlaceholder')}
                  aria-label={t('admin.products.cakeOptions.priceAria', { name: label })}
                  value={d.price}
                  onChange={(e) => update(i.id, { price: e.target.value })}
                />
              </Row>
            );
          })}
        </Group>
      ))}
      <SaveButton type="button" onClick={handleSave} disabled={saveMutation.isPending || anyInvalid}>
        {saveMutation.isPending ? t('admin.products.cakeOptions.saving') : t('admin.products.cakeOptions.save')}
      </SaveButton>
      {saved && <SavedText role="status">{t('admin.products.cakeOptions.saved')}</SavedText>}
      {error && <ErrorText role="alert">{resolveErrorBanner(error, t)}</ErrorText>}
    </div>
  );
};

export default ProductCakeOptionsPanel;
